/**
 * User-initiated driver-clear cascade for the filing-info dropdown chain,
 * shared by role0 (Filing Prerequisites) and role1 (Filing Info).
 *
 * The chain:
 *   Origin of company -> Types of company -> Basis of accounting standards
 *                                              -> Type of submission
 *
 * WHY THIS FILE EXISTS
 * role0 and role1 carry duplicated copies of five reactive-rule engines, and
 * divergence between them is a documented recurring defect class. Declaring the
 * cascade ONCE, here, means the two sheets cannot disagree about which fields
 * reset — only about how they apply it (~35 mirrored lines each: the
 * `isDriverClearCascading` guard field, the `setupDriverClearCascade` method
 * body, and its one-line registration in `ngOnInit`).
 *
 * ONLY DIRECT EDGES ARE DECLARED. `resolveDriverClearTargets` walks the
 * transitive closure, so "clearing Origin also clears Basis" is a consequence of
 * the two edges below rather than a third rule that can drift out of step. The
 * walk is driven by the DECLARED GRAPH, not by current values, so clearing
 * Origin still reaches Basis when Types of company was already empty.
 *
 * `ssmt-dei:BasisOfAccountingStandards... -> ssmt-dei:TypeOfSubmission` is
 * deliberately ABSENT. That derivation already has exactly one owner per sheet
 * (`setupTypeOfSubmissionAutoSet`), which clears the derived FS-MFRS/FS-MPERS
 * pair — and only that pair, never a KFI-* / FS-EPC value owned by another flow.
 * Callers clear Basis with `emitEvent: true` so that seam fires. Adding the edge
 * here would give one rule two owners.
 *
 * SCOPE: user-initiated clears only (the [showClear] X, the Delete key). The
 * extraction-null hydration path is separate, renders the dependent empty +
 * DISABLED + unvalidated, and must not route through here.
 *
 * Pure and dependency-free by requirement — `npm run check:validator-purity`
 * forbids imports in this folder.
 */
export const FILING_INFO_DRIVER_CLEAR_EDGES: Readonly<
  Record<string, readonly string[]>
> = {
  'ssmt-dei:OriginOfCompany': ['ssmt-dei:TypeOfCompany'],
  'ssmt-dei:TypeOfCompany': [
    'ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements',
  ],
};

/**
 * A dropdown reports "no selection" as `null` (PrimeNG's clear button and the
 * Delete key) or as `''` (our own programmatic resets). `undefined` covers a
 * control absent from a partial payload. Whitespace is NOT trimmed — a select
 * never emits it, and inventing that rule would hide real data.
 */
export function isClearedValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Every qname that must reset when `driverQname` is cleared, transitively,
 * breadth-first, parents before children, with the driver itself excluded.
 * Returns `[]` for a qname that drives nothing.
 *
 * The `visited` set makes the walk safe against a cycle should a future edge
 * introduce one.
 */
export function resolveDriverClearTargets(driverQname: string): string[] {
  const targets: string[] = [];
  const visited = new Set<string>([driverQname]);
  const queue: string[] = [...(FILING_INFO_DRIVER_CLEAR_EDGES[driverQname] ?? [])];

  while (queue.length > 0) {
    const qname = queue.shift()!;
    if (visited.has(qname)) {
      continue;
    }
    visited.add(qname);
    targets.push(qname);
    queue.push(...(FILING_INFO_DRIVER_CLEAR_EDGES[qname] ?? []));
  }

  return targets;
}
